import { useRef, useEffect, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const PROVIDER = process.env.REACT_APP_MAPS_PROVIDER || (MAPS_KEY ? "google" : "osm");

let mapsFailed = false;
const failListeners = new Set();
if (typeof window !== "undefined") {
  window.gm_authFailure = () => { mapsFailed = true; failListeners.forEach((f) => f()); };
}
const useMapsFailed = () => {
  const [failed, setFailed] = useState(mapsFailed);
  useEffect(() => {
    const f = () => setFailed(true);
    failListeners.add(f);
    return () => failListeners.delete(f);
  }, []);
  return failed;
};

export const MapsProvider = ({ children }) =>
  PROVIDER === "google" && MAPS_KEY ? <APIProvider apiKey={MAPS_KEY}>{children}</APIProvider> : <>{children}</>;

const AutoInput = ({ value, onChange, placeholder, testId, className, required }) => {
  const places = useMapsLibrary("places");
  const ref = useRef(null);

  useEffect(() => {
    if (!places || !ref.current) return;
    const ac = new places.Autocomplete(ref.current, {
      fields: ["formatted_address", "geometry", "name"],
    });
    const listener = ac.addListener("place_changed", () => {
      const p = ac.getPlace();
      const loc = p.geometry?.location;
      onChange({
        text: p.formatted_address || p.name || ref.current.value,
        lat: loc ? loc.lat() : null,
        lng: loc ? loc.lng() : null,
      });
    });
    return () => listener.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places]);

  return (
    <Input ref={ref} data-testid={testId} required={required} placeholder={placeholder}
      defaultValue={value?.text || ""} className={className}
      onChange={(e) => onChange({ text: e.target.value, lat: null, lng: null })} />
  );
};

const OsmAutoInput = ({ value, onChange, placeholder, testId, className, required }) => {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const handle = (text) => {
    onChange({ text, lat: null, lng: null });
    clearTimeout(timer.current);
    if (text.trim().length < 3) { setSugs([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/search", { params: { q: text } });
        setSugs(data);
        setOpen(data.length > 0);
      } catch { setSugs([]); }
    }, 450);
  };

  return (
    <div className="relative">
      <Input data-testid={testId} required={required} placeholder={placeholder} autoComplete="off"
        value={value?.text || ""} className={className}
        onChange={(e) => handle(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-black/10 rounded-2xl shadow-[0_6px_16px_rgba(0,0,0,0.12)] overflow-hidden">
          {sugs.map((s, i) => (
            <button key={s.place_id || s.text} type="button" data-testid={`geo-suggestion-${i}`}
              onMouseDown={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left px-4 py-2.5 text-sm hover:bg-black/5 truncate transition-colors duration-150">
              {s.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const LocationField = (props) => {
  const failed = useMapsFailed();
  if (PROVIDER === "google" && MAPS_KEY && !failed) return <AutoInput {...props} />;
  return <OsmAutoInput {...props} />;
};

const Directions = ({ from, to, onInfo }) => {
  const map = useMap();
  const routes = useMapsLibrary("routes");

  useEffect(() => {
    if (!map || !routes) return;
    const svc = new routes.DirectionsService();
    const renderer = new routes.DirectionsRenderer({
      map,
      polylineOptions: { strokeColor: "#1DAF5A", strokeWeight: 5 },
    });
    svc.route({
      origin: { lat: from.lat, lng: from.lng },
      destination: { lat: to.lat, lng: to.lng },
      travelMode: "DRIVING",
    }).then((res) => {
      renderer.setDirections(res);
      const leg = res.routes?.[0]?.legs?.[0];
      if (leg) onInfo?.(`${leg.distance.text} · ~${leg.duration.text}`);
    }).catch(() => {});
    return () => renderer.setMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, routes]);

  return null;
};

export const RidesPinMap = ({ rides, onSelect }) => {
  const divRef = useRef(null);
  useEffect(() => {
    if (!divRef.current) return;
    const map = L.map(divRef.current, { attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    const pts = [];
    rides.forEach((r) => {
      if (r.from_lat == null || r.from_lng == null) return;
      const mk = L.circleMarker([r.from_lat, r.from_lng],
        { radius: 9, color: "#ffffff", weight: 2, fillColor: "#1DAF5A", fillOpacity: 0.95 }).addTo(map);
      mk.bindTooltip(`${r.from_location.split(",")[0]} → ${r.to_location.split(",")[0]} · ${r.departure_time}`);
      mk.on("click", () => onSelect?.(r));
      pts.push([r.from_lat, r.from_lng]);
    });
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 13 });
    else map.setView([17.44, 78.49], 11);
    return () => map.remove();
  }, [rides, onSelect]);
  return <div ref={divRef} className="h-80 z-0" data-testid="rides-pin-map" />;
};

const GoogleRouteMap = ({ from, to }) => {
  const [info, setInfo] = useState(null);
  return (
    <MapsProvider>
      <div className="t-card overflow-hidden" data-testid="route-map">
        <div className="h-64">
          <Map defaultCenter={{ lat: from.lat, lng: from.lng }} defaultZoom={12}
            disableDefaultUI gestureHandling="greedy" zoomControl>
            <Directions from={from} to={to} onInfo={setInfo} />
          </Map>
        </div>
        {info && (
          <p data-testid="route-info" className="px-5 py-3 text-sm font-bold">Route: {info}</p>
        )}
      </div>
    </MapsProvider>
  );
};

const OsmRouteMap = ({ from, to }) => {
  const divRef = useRef(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!divRef.current) return;
    const map = L.map(divRef.current, { attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    const a = [from.lat, from.lng], b = [to.lat, to.lng];
    L.circleMarker(a, { radius: 8, color: "#1DAF5A", fillColor: "#1DAF5A", fillOpacity: 1 }).addTo(map);
    L.circleMarker(b, { radius: 8, color: "#12211C", fillColor: "#12211C", fillOpacity: 1 }).addTo(map);
    map.fitBounds([a, b], { padding: [40, 40] });
    api.get("/geo/route", { params: { from_lat: from.lat, from_lng: from.lng, to_lat: to.lat, to_lng: to.lng } })
      .then(({ data }) => {
        if (data.coords?.length) {
          const line = L.polyline(data.coords, { color: "#1DAF5A", weight: 5 }).addTo(map);
          map.fitBounds(line.getBounds(), { padding: [30, 30] });
        }
        setInfo(`${data.distance_km} km · ~${data.duration_min} min drive`);
      })
      .catch(() => {});
    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from.lat, from.lng, to.lat, to.lng]);

  return (
    <div className="t-card overflow-hidden" data-testid="route-map">
      <div ref={divRef} className="h-64 z-0" />
      {info && (
        <p data-testid="route-info" className="px-5 py-3 text-sm font-bold">Route: {info}</p>
      )}
    </div>
  );
};

export const RouteMap = ({ from, to }) => {
  const failed = useMapsFailed();
  if (from?.lat == null || from?.lng == null || to?.lat == null || to?.lng == null) return null;
  if (PROVIDER === "google" && MAPS_KEY && !failed) return <GoogleRouteMap from={from} to={to} />;
  return <OsmRouteMap from={from} to={to} />;
};
